import React, { useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  styled,
  Typography,
  useTheme,
} from '@mui/material'
import { ExpandMore, Person } from '@mui/icons-material'
import fullName from '../../../utils/fullName'
import FinancialSummary from './FinancialSummary'
import { FetchCommunityFinancialsResponseFinancialSummary } from '../../../swagger'
import DirectorDetails from './DirectorDetails'

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'white',
  boxShadow: '0 1px 3px rgba(5, 31, 66, 0.1)',
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: `${theme.spacing(0)} 0 ${theme.spacing(2)} 0`,
  },
}))

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2, 3),
  minHeight: 72,
  '&.Mui-expanded': {
    minHeight: 72,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    alignItems: 'center',
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: theme.palette.primary.main,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1),
    paddingLeft: theme.spacing(2),
  },
}))

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 3, 3, 3),
  backgroundColor: '#fafafa',
}))

interface DirectorAccordionProps {
  director: FetchCommunityFinancialsResponseFinancialSummary
  communityKey: number
}

const DirectorAccordion: React.FC<DirectorAccordionProps> = ({
  director,
  communityKey,
}) => {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)

  const handleChange = () => {
    setExpanded((prev) => {
      const next = !prev
      if (next) setHasLoaded(true)
      return next
    })
  }

  return (
    <StyledAccordion expanded={expanded} onChange={handleChange}>
      <StyledAccordionSummary
        expandIcon={
          <Box
            sx={{
              [theme.breakpoints.down('sm')]: {
                display: 'none',
              },
            }}
          >
            <ExpandMore />
          </Box>
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Person
            sx={{
              mr: 3,
              color: theme.palette.primary.main,
              fontSize: 28,
              [theme.breakpoints.down('sm')]: {
                display: 'none',
                mr: 0,
              },
            }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.primary.main,
                fontFamily: 'sweet-sans-pro, sans-serif',
                fontWeight: 800,
                fontSize: '1.125rem',
                [theme.breakpoints.down('sm')]: {
                  fontSize: '1rem',
                },
              }}
            >
              {fullName({
                firstName: director.firstName,
                lastName: director.lastName,
              })}{' '}
              ({director.roleName})
            </Typography>

            <FinancialSummary
              totalDirectorOwe={director.totalDirectorOwe}
              totalFamiliesOwe={director.totalFamiliesOwe}
              totalTutorsOwe={director.totalTutorsOwe}
            />
          </Box>
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              alignSelf: 'stretch',
              borderLeft: `1px solid ${theme.palette.divider}`,
              ml: 2,
              pl: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                transition: 'transform 0.2s ease-in-out',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <ExpandMore />
            </Box>
          </Box>
        </Box>
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        {hasLoaded && (
          <DirectorDetails
            communityKey={communityKey}
            actorKey={director.actorKey}
          />
        )}
      </StyledAccordionDetails>
    </StyledAccordion>
  )
}

export default DirectorAccordion
